import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { User } from '@adornis/buchungsheld-db/entities/user.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { stateful, useEffect, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { useGeneratedForm } from '@adornis/generated-form/use-generated-form.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { html, nothing } from 'lit';
import { of } from 'rxjs';
import * as m from '../../../../localization/out/messages.js';
import { getAndEnsureUserDataOfUserID } from '../../../api/user.js';
import { MWUserData } from '../../../db/user.js';
import { CtaPillButton } from '../../components/cta-pill-button.js';
import { formatFormError, useScrollToError } from '../../components/format-form-error.js';
import { translateValidationError } from '../../components/form-validation.js';

// Hand-written rather than `entityValidator(MWUserData)`: both flags are plain
// `Maybe<boolean>` on the entity with no @validate, deliberately — they are only
// required *at this step*, and decorating them would make every other MWUserData
// save (steps 1 and 2, which run before this one) fail.
const TERMS_VALIDATOR = {
  privacyConsent: nonOptional({ translationKey: 'validation_terms_privacy_required' }),
  accuracyConfirm: nonOptional({ translationKey: 'validation_terms_accuracy_required' }),
};

export const RegisterOnboardingTerms = stateful(
  ({ onFinished, onBack }: { onFinished: () => void; onBack: () => void }) => {
    const user = useObservable(() => currentUser<User>(), []);

    const userDataQuery = useQuery(() => {
      if (!user?._id) return of(null);
      return getAndEnsureUserDataOfUserID(user._id)(selectionSet(() => MWUserData, 2));
    }, [user?._id]);

    const [placeholder] = useState(new MWUserData({}));
    const userData = userDataQuery.value ?? placeholder;
    const [saving, setSaving] = useState(false, []);
    const [consentError, setConsentError] = useState<string | null>(null, []);
    useScrollToError(consentError);

    const { form, renderField } = useGeneratedForm(
      userData,
      {
        translationPrefix: 'register_onboarding_terms_form_',
        validator: TERMS_VALIDATOR,
        translateError: translateValidationError,
      },
      [userData],
    );

    useEffect(() => {
      if (consentError && userData.privacyConsent && userData.accuracyConfirm) {
        setConsentError(null);
      }
    }, [consentError, userData.privacyConsent, userData.accuracyConfirm]);

    const handleNext = async () => {
      setConsentError(null);
      setSaving(true);
      try {
        // The validator now enforces both checkboxes, so this replaces the manual
        // `if (!privacyConsent || !accuracyConfirm)` guard — and unlike that guard it
        // also marks the *individual* box that is missing.
        const err = await form.validate();
        if (err) {
          setConsentError(formatFormError(err, 'register_onboarding_terms_form_'));
          return;
        }
        await userData.save();
        onFinished();
      } catch (e) {
        console.error(e);
        void XSnackbar.show(formatFormError(e, 'register_onboarding_terms_form_'));
      } finally {
        setSaving(false);
      }
    };

    return html`
      <div class="space-y-4">
        <h3 class="card-title">${m.register_onboarding_terms_title()}</h3>
        <div class="text-sm opacity-80">${m.register_onboarding_terms_intro()}</div>
        <div class="prose text-sm opacity-90">
          <p>${m.register_onboarding_terms_p1()}</p>
          <p>${m.register_onboarding_terms_p2()}</p>
        </div>
        ${
          consentError
            ? html`<div class="alert alert-error" data-error-anchor>
                <span class="icon-[material-symbols--error]"></span>
                <span>${consentError}</span>
              </div>`
            : nothing
        }
        <div class="flex items-start gap-1">
          ${renderField(['privacyConsent'])}
          <span class="text-error">*</span>
        </div>
        <div class="flex items-start gap-1">
          ${renderField(['accuracyConfirm'])}
          <span class="text-error">*</span>
        </div>
        <div class="text-xs opacity-70">${m.register_onboarding_terms_button_note()}</div>
      </div>
      <div class="mt-6 flex flex-col sm:flex-row items-center sm:items-start justify-between gap-2">
        <button class="btn w-full sm:w-auto" @click=${() => onBack()}>${m.back()}</button>
        ${saving ? html`<span class="loading loading-spinner"></span>` : nothing}
        ${CtaPillButton({
          label: m.register_onboarding_terms_next_button(),
          onClick: handleNext,
          disabled: saving,
        })}
      </div>
    `;
  },
);
